import { SlideshowSelectEvent } from './events.js';
import { debounce } from './utilities.js';

/**
 * Handles showing the previous button in resource-list carousels
 * after the next button has been clicked at least once.
 * Also handles continuous ticker-style scrolling for autoplay carousels.
 */
class ResourceListCarousel {
  #initializedCarousels = new WeakSet();
  #continuousCarousels = new WeakSet();

  init() {
    // Find all resource-list carousels on the page
    const carousels = document.querySelectorAll('.resource-list__carousel');

    for (const carousel of carousels) {
      // Skip if already initialized
      if (this.#initializedCarousels.has(carousel)) continue;

      const slideshow = carousel.querySelector('slideshow-component');
      if (!slideshow) continue;

      const previousButton = carousel.querySelector(
        'slideshow-arrows .slideshow-control--previous'
      );
      if (!previousButton) continue;

      // Mark as initialized
      this.#initializedCarousels.add(carousel);

      // Check if this carousel should use continuous scroll (autoplay enabled)
      if (slideshow.hasAttribute('autoplay') && !this.#continuousCarousels.has(carousel)) {
        this.#initContinuousScroll(
          /** @type {HTMLElement} */ (carousel),
          /** @type {HTMLElement} */ (slideshow)
        );
        this.#continuousCarousels.add(carousel);
      }

      // Listen for slideshow select events
      slideshow.addEventListener(SlideshowSelectEvent.eventName, (event) => {
        const selectEvent = /** @type {SlideshowSelectEvent} */ (event);
        if (!selectEvent.detail.userInitiated) return;

        const { index, previousIndex } = selectEvent.detail;

        // If we moved forward (next button was clicked), show the previous button
        if (index > previousIndex) {
          previousButton.classList.add('show-previous');
        }
      });
    }
  }

  /**
   * @param {HTMLElement} carousel
   * @param {HTMLElement} slideshow
   */
  #initContinuousScroll(carousel, slideshow) {
    const slidesContainer = /** @type {HTMLElement | null} */ (
      carousel.querySelector('slideshow-slides')
    );
    if (!slidesContainer) return;

    // Disable discrete autoplay in slideshow component
    slideshow.setAttribute('data-continuous-scroll', 'true');

    // Add continuous scroll class
    carousel.classList.add('resource-list__carousel--continuous');

    // Duplicate slides for seamless loop
    this.#duplicateSlides(slidesContainer);

    // Calculate and set scroll speed after a brief delay to ensure DOM is updated
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#setScrollSpeed(carousel, slidesContainer);
      });
    });

    // Handle resize to recalculate speed
    const handleResize = debounce(() => {
      this.#setScrollSpeed(carousel, slidesContainer);
    }, 250);

    window.addEventListener('resize', handleResize);

    // Pause animation on hover
    carousel.addEventListener('mouseenter', () => {
      const slides = /** @type {HTMLElement | null} */ (
        carousel.querySelector('slideshow-slides')
      );
      if (slides) {
        slides.style.animationPlayState = 'paused';
      }
    });

    carousel.addEventListener('mouseleave', () => {
      const slides = /** @type {HTMLElement | null} */ (
        carousel.querySelector('slideshow-slides')
      );
      if (slides) {
        slides.style.animationPlayState = 'running';
      }
    });
  }

  /**
   * @param {HTMLElement} slidesContainer
   */
  #duplicateSlides(slidesContainer) {
    // Check if already duplicated
    if (slidesContainer.dataset.duplicated === 'true') return;

    // Clone all slides and append to the same container for seamless loop
    const slides = Array.from(slidesContainer.children);
    
    for (const slide of slides) {
      const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));
      // Remove ref attributes from cloned slides
      for (const el of clone.querySelectorAll('[ref]')) {
        /** @type {HTMLElement} */ (el).removeAttribute('ref');
      }
      clone.setAttribute('aria-hidden', 'true');
      slidesContainer.appendChild(clone);
    }

    // Mark as duplicated
    slidesContainer.dataset.duplicated = 'true';
  }

  /**
   * @param {HTMLElement} carousel
   * @param {HTMLElement} slidesContainer
   */
  #setScrollSpeed(carousel, slidesContainer) {
    const carouselWidth = carousel.offsetWidth;
    const slidesWidth = slidesContainer.scrollWidth;
    
    if (slidesWidth === 0) return;

    // Calculate speed: ~1-2 pixels per second for slow ticker
    // Speed in seconds = distance / pixelsPerSecond
    const pixelsPerSecond = 1.5; // Slow ticker speed
    const speed = Math.ceil(slidesWidth / 2 / pixelsPerSecond); // Divide by 2 since we're animating 50% of content

    carousel.style.setProperty('--carousel-ticker-speed', `${speed}s`);
  }
}

const resourceListCarousel = new ResourceListCarousel();

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => resourceListCarousel.init());
} else {
  resourceListCarousel.init();
}

// Also initialize for dynamically added content (e.g., section hydration)
document.addEventListener('section:hydrated', () => resourceListCarousel.init());
